import React, { useState, useCallback } from 'react';
import InlineTranslationEdit from './InlineTranslationEdit';
import RichContentRenderer from './RichContentRenderer';
import { useContent } from '../contexts/ContentContext';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useSectionControl } from '../contexts/SectionControlContext';
import { useTranslations } from '../contexts/TranslationContext';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up.js';
import Settings from 'lucide-react/dist/esm/icons/settings.js';

const BG_PRESETS = [
  { labelKey: 'admin.heroSettings.white', label: 'White', value: '#ffffff', style: { backgroundColor: '#ffffff' } },
  { labelKey: 'admin.heroSettings.clear', label: 'Clear', value: 'transparent', style: { backgroundImage: 'repeating-conic-gradient(#ccc 0% 25%, white 0% 50%)', backgroundSize: '4px 4px' } },
  { labelKey: 'admin.heroSettings.black', label: 'Black', value: '#000000', style: { backgroundColor: '#000000' } },
  { labelKey: 'admin.heroSettings.dark', label: 'Dark', value: '#1a1a1a', style: { backgroundColor: '#1a1a1a' } },
];

function SectionSettingsPanel({ section }) {
  const { updateConfig } = useSectionControl();
  const { t } = useTranslations();
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState(() => {
    const bg = section?.config?.background || '#ffffff';
    const isPreset = BG_PRESETS.some(p => p.value === bg);
    return isPreset ? bg : bg; // custom color stored directly
  });
  const [customHex, setCustomHex] = useState(() => {
    const bg = section?.config?.background || '#ffffff';
    const isPreset = BG_PRESETS.some(p => p.value === bg);
    return isPreset ? '#6366f1' : bg;
  });

  // Auto-save on change
  const handleSelect = useCallback(async (value) => {
    setSelected(value);
    try {
      await updateConfig(section.id, { background: value });
    } catch (err) {
      // silent
    }
  }, [section, updateConfig]);

  const handleCustomColor = useCallback(async (hex) => {
    setCustomHex(hex);
    setSelected(hex);
    try {
      await updateConfig(section.id, { background: hex });
    } catch (err) {
      // silent
    }
  }, [section, updateConfig]);

  return (
    <div className="absolute top-0 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center">
      <div className="rounded-b-xl bg-black/70 backdrop-blur-md border border-t-0 border-white/20 px-3 pt-2 pb-3 flex flex-col gap-2.5 min-w-[200px] shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <span className="text-[10px] font-semibold text-white/60 uppercase tracking-wider flex items-center gap-1">
            <Settings className="w-3 h-3" /> {t('admin.contentBlockSettings.title', 'Settings')}
          </span>
          <button
            onClick={() => setExpanded(v => !v)}
            className="p-0.5 rounded hover:bg-white/15 text-white/70 hover:text-white transition-colors"
            aria-label={t('admin.contentBlockSettings.title', 'Settings')}
            title={t('admin.contentBlockSettings.title', 'Settings')}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {expanded && (
          <>
            {/* Section background */}
            <div>
              <span className="text-xs text-white block mb-1.5">{t('admin.contentBlockSettings.sectionBackground', 'Section background')}</span>
              <div className="flex gap-1.5 flex-wrap items-center">
                {BG_PRESETS.map(preset => {
                  const presetLabel = t(preset.labelKey, preset.label);
                  return (
                  <button
                    key={preset.value}
                    title={presetLabel}
                    aria-label={`${t('admin.contentBlockSettings.sectionBackground', 'Section background')} ${presetLabel}`}
                    onClick={() => handleSelect(preset.value)}
                    className={`w-5 h-5 rounded-full border-2 transition-all ${
                      selected === preset.value
                        ? 'border-indigo-400 scale-110'
                        : 'border-white/40'
                    }`}
                    style={{ ...preset.style, backgroundSize: '4px 4px' }}
                  />
                  );
                })}
                {/* Custom color picker (rainbow circle) */}
                <label
                  title={t('admin.heroSettings.customColor', 'Custom color')}
                  aria-label={t('admin.heroSettings.customColor', 'Custom color')}
                  className={`relative w-5 h-5 rounded-full border-2 cursor-pointer transition-all overflow-hidden ${
                    !BG_PRESETS.some(p => p.value === selected)
                      ? 'border-indigo-400 scale-110'
                      : 'border-white/40'
                  }`}
                  style={{ backgroundImage: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)' }}
                >
                  <input
                    type="color"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    aria-label={t('admin.heroSettings.customColor', 'Custom color')}
                    value={customHex.startsWith('#') ? customHex : '#6366f1'}
                    onChange={e => handleCustomColor(e.target.value)}
                  />
                </label>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function CustomContentBlockSection({ section, config = {} }) {
  const { isAdmin } = useIsAdmin();
  const { c, resolveContentValue, updateContent } = useContent();
  const { t } = useTranslations();
  const sectionControl = useSectionControl();
  const mergedConfig = { ...(section?.config || {}), ...config };
  const contentKey = mergedConfig.contentKey || `customPages.${section?.page_name || 'page'}.${section?.section_type || 'content'}`;
  const contentValue = c(contentKey);
  const renderedContentValue = resolveContentValue(contentValue, { currentKey: contentKey, editMode: isAdmin });
  const hasContent = String(contentValue || '').trim().length > 0;
  const background = mergedConfig.background || '#ffffff';
  const textColor = mergedConfig.textColor || '#111827';
  const label = mergedConfig.label || t('admin.contentBlockSettings.blankContent', 'Blank Content');

  // When an admin adds content to a previously-empty block, automatically
  // reveal the section so it publishes as visible. Seeded content blocks
  // default to is_visible:false, so without this the published content would
  // stay hidden from public visitors (admins bypass the visibility filter).
  const handleContentUpdate = useCallback((key, value, lang) => {
    updateContent(key, value, lang);
    const becameNonEmpty = String(value || '').trim().length > 0;
    if (becameNonEmpty && section?.is_visible === false && sectionControl?.setVisibility) {
      sectionControl.setVisibility(section.id, true);
    }
  }, [updateContent, section, sectionControl]);

  if (isAdmin) {
    return (
      <div
        className="relative w-full rounded-2xl border-2 border-dashed border-emerald-300 dark:border-emerald-700 px-6 py-10 text-left shadow-sm overflow-visible"
        style={{ background, color: textColor }}
      >
        <SectionSettingsPanel section={section} />
        {!hasContent && (
          <div className="mb-3 text-xs font-bold uppercase tracking-wider opacity-50">
            {label}
          </div>
        )}
        <InlineTranslationEdit
          translationKey={contentKey}
          value={contentValue}
          fallback={t('admin.contentBlockSettings.clickToAdd', 'Click to add content')}
          editMode
          multiline
          onUpdate={handleContentUpdate}
          className="text-base leading-relaxed"
        />
      </div>
    );
  }

  if (!hasContent) return null;

  const containsHtml = /<[^>]+>/.test(renderedContentValue);
  return (
    <div
      className="w-full rounded-2xl px-6 py-10 text-left shadow-sm"
      style={{ background, color: textColor }}
    >
      {containsHtml ? (
        <RichContentRenderer
          html={renderedContentValue}
          variant="editorial"
          className="w-full"
        />
      ) : (
        <p className="m-0 whitespace-pre-wrap text-base leading-relaxed">
          {renderedContentValue}
        </p>
      )}
    </div>
  );
}
